import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import {
  BrowserIcon,
  ClockIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  MoviePlayIcon,
  PdfIcon,
} from "../../../../icons/reactIcons";
import "./ResourceDetails.css";
import noImage from "../../../../asstets/images/no_image.png";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import {
  deleteResourceThunk,
  fetchResourceByIdThunk,
  fetchResourceListThunk,
} from "../../../../redux/slices/resourceSlice";
import Loader from "../../../../components/custom-loader/Loader";
import ConfirmUserActionModal from "../../../../components/confirm-modal/ConfirmUserActionModal";

const ResourceDetails = () => {
  const navigate = useNavigate();
  const { resourceId } = useParams<{ resourceId: string }>();
  const dispatch = useAppDispatch();

  const { selectedResource, loading } = useAppSelector(
    (state) => state.resource,
  );
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (resourceId) {
      dispatch(fetchResourceByIdThunk(resourceId));
    }
  }, [resourceId, dispatch]);

  const handleDeleteResource = async () => {
    if (!resourceId) return;

    try {
      setSubmitting(true);

      const result = await dispatch(deleteResourceThunk(resourceId));

      if (deleteResourceThunk.fulfilled.match(result)) {
        // Refetch updated list
        dispatch(fetchResourceListThunk({ page: 1, limit: 100 }));
        setOpenDeleteModal(false);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setSubmitting(false);
      navigate("/resources");
    }
  };

  const breadcrumbItems = [
    { label: "Resource", onClick: () => navigate("/resources") },
    { label: "Resource Details", disabled: true },
  ];

  if (loading) {
    return <Loader />;
  }

  if (!selectedResource) return null;

  return (
    <div>
      <Breadcrumb items={breadcrumbItems} />

      <div className="resource-details-header">
        <h1 className="page-title">Resource Details</h1>
        <div className="resource-header-action">
          <button
            className="resource-edit-btn"
            onClick={() => navigate(`/resources/edit/${resourceId}`)}
          >
            <EditIcon /> Edit
          </button>
          <button
            className="resource-dlt-btn"
            onClick={() => setOpenDeleteModal(true)}
          >
            <DeleteIcon /> Delete
          </button>
        </div>
      </div>

      <div className="resource-details-container">
        <div className="resource-header-area">
          <div className="resource-details-title-area">
            <h2>{selectedResource.name}</h2>
          </div>
        </div>

        <div className="resource-image">
          <img
            src={selectedResource.fileUrl || noImage}
            alt={selectedResource.altText || selectedResource.name}
          />
        </div>

        {selectedResource.document?.length > 0 && (
          <div className="attachments-section">
            <h4>Other Attachments</h4>

            <div className="attachment-list">
              {selectedResource.document.map((item, index) => (
                <div className="attachment-item" key={index}>
                  <div className="attachment-item-icon">
                    {item.documentType === "file" && <PdfIcon />}
                    {item.documentType === "site" && <BrowserIcon />}
                    {item.documentType === "video" && <MoviePlayIcon />}
                  </div>

                  <span>
                    {item.documentType === "file" && "Document - "}
                    {item.documentType === "site" && "Website - "}
                    {item.documentType === "video" && "Video - "}
                  </span>

                  <a
                    href={
                      item.documentType === "file"
                        ? item.documentFileUrl
                        : item.documentLink
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.documentType === "video"
                      ? "Watch now"
                      : item.documentType === "site"
                        ? "Visit now"
                        : "Read now"}
                  </a>

                  {item.documentType === "video" && item.documentDuration && (
                    <span className="video-duration">
                      <ClockIcon /> {item.documentDuration}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="resource-description">
          {selectedResource.description && (
            <div
              dangerouslySetInnerHTML={{
                __html: selectedResource.description,
              }}
            />
          )}
        </div>
      </div>
      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={selectedResource.name}
          loading={submitting}
          onClose={() => {
            setOpenDeleteModal(false);
          }}
          onConfirm={handleDeleteResource}
        />
      )}
    </div>
  );
};

export default ResourceDetails;
